import React from "react";
import { useNavigate } from "react-router-dom";
import "./WorkingProfessionals.css";

export default function WorkingProfessionals() {
  const navigate = useNavigate();

  const professionalRoadmap = [
    {
      topic: "Linux",
      percentage: 5,
      startDay: 1,
      endDay: 9,
      totalDays: 9,
      focus:
        "Terminal basics, file system, commands, permissions, process basics, and developer workflow.",
      checkpoints: [
        "Linux command assignment",
        "Terminal practice task",
        "Basic environment setup review",
      ],
    },
    {
      topic: "HTML",
      percentage: 6,
      startDay: 10,
      endDay: 27,
      totalDays: 18,
      focus:
        "HTML structure, semantic tags, forms, tables, media, SEO basics, and page structure.",
      checkpoints: ["HTML page assignment", "Resume webpage mini project", "Structure review"],
    },
    {
      topic: "CSS",
      percentage: 6,
      startDay: 28,
      endDay: 45,
      totalDays: 18,
      focus:
        "Selectors, box model, flexbox, grid, responsive design, animations, and modern UI styling.",
      checkpoints: ["Landing page assignment", "Responsive layout project", "UI review session"],
    },
    {
      topic: "Python",
      percentage: 11,
      startDay: 46,
      endDay: 86,
      totalDays: 41,
      focus:
        "Python fundamentals, functions, OOP, files, error handling, modules, scripting, and problem solving.",
      checkpoints: ["Python practice assignments", "Automation mini project", "Coding mock discussion"],
    },
    {
      topic: "FastAPI",
      percentage: 5,
      startDay: 87,
      endDay: 104,
      totalDays: 18,
      focus:
        "API creation, routing, request/response models, validation, authentication basics, and backend structure.",
      checkpoints: ["FastAPI assignment", "CRUD API mini project", "API testing review"],
    },
    {
      topic: "JavaScript",
      percentage: 5,
      startDay: 105,
      endDay: 122,
      totalDays: 18,
      focus:
        "Variables, functions, DOM, events, async JavaScript, fetch API, and browser logic.",
      checkpoints: ["JavaScript assignment", "DOM mini project", "Frontend logic review"],
    },
    {
      topic: "React.js",
      percentage: 9,
      startDay: 123,
      endDay: 152,
      totalDays: 30,
      focus:
        "Components, props, state, hooks, routing, API integration, forms, and frontend project building.",
      checkpoints: ["React assignment", "Frontend project", "Mock project explanation"],
    },
    {
      topic: "MongoDB",
      percentage: 5,
      startDay: 153,
      endDay: 163,
      totalDays: 11,
      focus:
        "Collections, documents, CRUD, schema design basics, aggregation basics, and backend integration.",
      checkpoints: ["MongoDB CRUD assignment", "Database integration task", "Data model review"],
    },
    {
      topic: "Docker",
      percentage: 6,
      startDay: 164,
      endDay: 184,
      totalDays: 21,
      focus:
        "Containers, images, Dockerfile, volumes, networks, compose basics, and deployment mindset.",
      checkpoints: ["Docker assignment", "Containerize project", "Deployment review"],
    },
    {
      topic: "Kubernetes",
      percentage: 6,
      startDay: 185,
      endDay: 205,
      totalDays: 21,
      focus:
        "Pods, deployments, services, scaling basics, config, and production system understanding.",
      checkpoints: ["Kubernetes basics task", "Deployment YAML practice", "Infra mock discussion"],
    },
    {
      topic: "GitHub",
      percentage: 5,
      startDay: 206,
      endDay: 215,
      totalDays: 10,
      focus:
        "Repositories, pull requests, README writing, project hosting, and portfolio presentation.",
      checkpoints: ["GitHub portfolio setup", "README review", "Profile improvement"],
    },
    {
      topic: "Artificial Intelligence",
      percentage: 16,
      startDay: 216,
      endDay: 270,
      totalDays: 55,
      focus:
        "AI fundamentals, machine learning, deep learning, GenAI, LLMs, NLP, computer vision, model usage, and AI project building.",
      checkpoints: [
        "AI assignment",
        "ML/AI mini project",
        "GenAI project",
        "AI project presentation",
        "Technical mock interview",
      ],
    }
  ];

  const dailyRoutine = [
    { time: "7:00 AM – 7:30 AM", task: "Focused Lecture / Concept Revision", icon: "🎓", type: "Preparation" },
    { time: "7:30 AM – 7:50 AM", task: "Notes + Daily Technical Goal", icon: "📝", type: "Preparation" },
    { time: "7:50 AM – 8:00 AM", task: "Work Preparation", icon: "🌅", type: "Life Balance" },
    { time: "8:00 AM – 8:00 PM", task: "Office / Job / Professional Work", icon: "🏢", type: "Work Time" },
    { time: "8:00 PM – 8:30 PM", task: "Dinner + Refresh", icon: "🍱", type: "Life Balance" },
    { time: "8:30 PM – 9:15 PM", task: "Project Building / Coding Practice", icon: "🚀", type: "Preparation" },
    { time: "9:15 PM – 9:45 PM", task: "Doubt Solving / Peer Discussion / Revision", icon: "🔁", type: "Preparation" },
    { time: "9:45 PM – 10:00 PM", task: "Next-Day Planning + Sleep Preparation", icon: "🌙", type: "Life Balance" },
  ];

  const everyTopicIncludes = [
    "Guided lecture learning and study flow",
    "Notes building and daily revision",
    "Assignments after topic completion",
    "Doubt solving and discussion rounds",
    "Mini projects and implementation tasks",
    "Weekend review and progress tracking",
    "Mock explanation or interview practice",
    "GitHub / portfolio update wherever applicable",
  ];

  const preparationTasks = [
    {
      icon: "🎓",
      name: "Guided Lecture Session",
      text: "Structured lecture study, mentor-guided topic flow, and concept understanding.",
    },
    {
      icon: "📝",
      name: "Notes Building",
      text: "Writing important points, examples, commands, syntax, mistakes, and interview notes.",
    },
    {
      icon: "🔁",
      name: "Concept Revision",
      text: "Revising previous lectures, notes, weak areas, assignments, and important concepts.",
    },
    {
      icon: "💻",
      name: "Coding Practice",
      text: "Hands-on coding, examples, implementation, debugging, and regular practice tasks.",
    },
    {
      icon: "📌",
      name: "Assignment Work",
      text: "Completing assigned tasks, topic exercises, submissions, and improvement work.",
    },
    {
      icon: "🚀",
      name: "Project Building",
      text: "Building portfolio projects, GitHub updates, deployment work, and project explanation.",
    },
    {
      icon: "❓",
      name: "Doubt Solving",
      text: "Clearing pending doubts with mentor or team support during available guidance windows.",
    },
    {
      icon: "🤝",
      name: "Peer Discussion",
      text: "Discussion with other learners, explanation practice, group learning, and confidence building.",
    },
  ];

  const lifeBalanceTasks = [
    {
      icon: "🏢",
      name: "Office / Job Time",
      text: "8 AM to 8 PM remains protected for office work, meetings, commute, and professional responsibilities.",
    },
    {
      icon: "🌅",
      name: "Morning Planning",
      text: "Quick planning before work to keep the day focused and reduce study confusion.",
    },
    {
      icon: "🍱",
      name: "Meals + Refresh",
      text: "Dinner, short break, personal recovery, and mental reset before night study.",
    },
    {
      icon: "🌙",
      name: "Sleep Discipline",
      text: "Early sleep planning to protect the next morning study routine.",
    },
  ];

  const jobSprint = [
    {
      title: "Career Positioning",
      image: "/placement/positioning.png",
      items: [
        "Identify suitable roles based on current experience and new skills",
        "Choose target direction: AI, Full Stack, Data, Backend, or GenAI",
        "Create a strong upgrade story for recruiters",
        "Fix weak points before serious job applications",
      ],
    },
    {
      title: "Resume, LinkedIn & GitHub Upgrade",
      image: "/placement/resume-linkedin.png",
      items: [
        "Prepare ATS-friendly resume",
        "Connect current job experience with new AI and software skills",
        "Polish LinkedIn headline, about section, and profile",
        "Clean GitHub with proper README and project links",
      ],
    },
    {
      title: "Project Explanation Confidence",
      image: "/placement/project-demo.png",
      items: [
        "Practice explaining major projects clearly",
        "Prepare architecture and workflow explanation",
        "Build answers for challenges, bugs, and improvements",
        "Create demo-ready project presentation flow",
      ],
    },
    {
      title: "Technical Interview Preparation",
      image: "/placement/technical-interview.png",
      items: [
        "Revise core subjects based on target role",
        "Practice Python, JavaScript, React, backend, database, and AI questions",
        "Attend project-based technical mock interviews",
        "Correct weak areas after every mock round",
      ],
    },
    {
      title: "HR & Communication Training",
      image: "/placement/hr-communication.png",
      items: [
        "Prepare strong self-introduction",
        "Explain career switch or upgrade clearly",
        "Practice salary expectation discussion",
        "Prepare HR, behavioural, and final round answers",
      ],
    },
    {
      title: "Job Application Strategy",
      image: "/placement/job-application.png",
      items: [
        "Select target companies and suitable roles",
        "Track daily and weekly job applications",
        "Prepare recruiter messages and emails",
        "Use LinkedIn, Naukri, company career pages, and referrals",
      ],
    },
    {
      title: "Mock Interviews & Panel Reviews",
      image: "/placement/mock-interview.png",
      items: [
        "Regular mock interview practice",
        "Panel discussion and explanation rounds",
        "Resume walkthrough practice",
        "Feedback-based improvement plan after each session",
      ],
    },
    {
      title: "Final Push & Follow-up",
      image: "/placement/final-push.png",
      items: [
        "Analyze interview feedback",
        "Plan follow-up message strategy",
        "Discuss offer and next steps",
        "Continue support until the professional becomes transition-ready",
      ],
    },
  ];

  return (
    <main className="W-working-page">
      {/* HERO */}
      <section className="W-working-hero">
        <div className="W-working-hero-content">
          <p className="section-kicker">WORKING PROFESSIONALS ROADMAP</p>

          <h1>A 1.5-year career upgrade roadmap for working professionals.</h1>

          <p>
            Continue your job while building AI, Full Stack, Data Science, GenAI,
            projects, portfolio, and interview readiness through a disciplined study system.
          </p>

          <div className="W-salary-band">
            <p className="section-kicker salary-kicker">Target preparation range</p>
            <strong>Up to ₹35 LPA</strong>
            <small>
              Based on current experience, consistency, project quality, interview
              performance, market conditions, role fit, and career transition readiness.
            </small>
          </div>

          <div className="W-working-hero-actions">
            <button onClick={() => navigate("/counselling?roadmap=working-professionals")}>
              Apply for Counselling
            </button>
            <a href="#working-roadmap">View 9-Month Roadmap</a>
          </div>
        </div>
      </section>

      {/* PRINCIPLE */}
      <section className="W-work-section">
        <div className="W-work-principle-box">
          <div>
            <p className="section-kicker">WORK + STUDY DISCIPLINE PRINCIPLE</p>

            <h2>
              Working professionals are busy. We convert limited time into a career
              upgrade system.
            </h2>

            <p>
              The goal is not to leave the job and study randomly. The goal is to use
              early morning and night hours with discipline: attend guided lecture
              sessions, revise, code, complete assignments, build projects, clear
              doubts, and prepare for better career opportunities step-by-step.
            </p>
          </div>

          <div className="W-mentor-clarity-card">
            <h3>Guided, not left alone.</h3>

            <p>
              Working professionals already carry job pressure, deadlines, and personal
              responsibilities. B. Akshay personally guides them on career direction,
              AI industry clarity, discipline, study mindset, and important high-level
              doubts.
            </p>

            <p>
              Along with mentor guidance, the StudyAIPlacement team supports professionals
              through assignments, projects, mock interviews, panel discussions, resume
              reviews, LinkedIn/GitHub improvement, and placement preparation.
            </p>

            <p>
              The goal is to help serious working professionals upgrade without
              confusion, burnout, or random learning.
            </p>
          </div>
        </div>
      </section>

      {/* DAILY TASK SYSTEM */}
      <section className="W-work-section">
        <div className="W-daily-task-system">
          <div className="W-work-heading W-compact">
            <p className="section-kicker">DAILY TASK SYSTEM</p>

            <h2>Small daily blocks. Serious long-term upgrade.</h2>

            <p>
              Working professionals follow a focused morning and night study system.
              The day is divided into Guided Lecture Sessions, Notes Building, Revision,
              Coding Practice, Assignment Work, Project Building, Doubt Solving, and
              Peer Discussion while keeping 8 AM to 8 PM free for office work.
            </p>
          </div>

          <div className="W-task-system-columns">
            <div className="W-task-system-panel">
              <h3>Preparation related tasks</h3>

              <div className="W-task-chip-grid">
                {preparationTasks.map((task) => (
                  <div className="W-task-chip-card" key={task.name}>
                    <div className="W-task-icon">{task.icon}</div>
                    <span>{task.name}</span>
                    <p>{task.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="W-task-system-panel W-life">
              <h3>Work and life balance</h3>

              <div className="W-task-chip-grid">
                {lifeBalanceTasks.map((task) => (
                  <div className="W-task-chip-card" key={task.name}>
                    <div className="W-task-icon">{task.icon}</div>
                    <span>{task.name}</span>
                    <p>{task.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DAILY ROUTINE */}
      <section className="W-work-section">
        <div className="W-work-heading">
          <p className="section-kicker">MORNING + NIGHT STUDY ROUTINE</p>

          <h2>A practical routine for people already doing a job.</h2>

          <p>
            The timetable keeps office hours free from 8 AM to 8 PM. Study happens
            from 7 AM before work and 8 PM to 10 PM through lecture sessions,
            revision, coding, assignments, projects, doubts, and next-day planning.
          </p>
        </div>

        <div className="W-routine-grid">
          {dailyRoutine.map((item) => (
            <div
              className={`W-routine-card ${
                item.type === "Work Time"
                  ? "W-work-routine-card"
                  : item.type === "Life Balance"
                    ? "W-life-routine-card"
                    : "W-prep-routine-card"
              }`}
              key={item.time}
            >
              <div className="W-routine-card-top">
                <span>{item.time}</span>
                <small>{item.type}</small>
              </div>

              <div className="W-routine-task-line">
                <b>{item.icon}</b>
                <p>{item.task}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="W-weekend-system">
          <div>
            <strong>Saturday focused review</strong>
            <p>
              Weekly revision, assignment review, project checkpoint, panel discussion,
              mock interview, and progress tracking.
            </p>
          </div>

          <div>
            <strong>Sunday recovery + light revision</strong>
            <p>
              Personal balance, recovery, family time, and optional light revision so
              professionals can stay consistent for the long journey.
            </p>
          </div>
        </div>
      </section>

      {/* ROADMAP */}
      <section className="W-work-section" id="working-roadmap">
        <div className="W-work-heading">
          <p className="section-kicker">UP TO 9-MONTH SKILL ROADMAP</p>

          <h2>Every phase supports a realistic career transition.</h2>

          <p>
            This 9-month roadmap gives working professionals enough time to build
            strong fundamentals, frontend, backend, databases, DevOps, mobile
            development, AI, Data Science, GenAI, projects, portfolio, and interview
            confidence without disturbing their current job.
          </p>
        </div>

        <div className="W-roadmap-unlock-list">
          {professionalRoadmap.map((item, index) => (
            <div className="W-unlock-card" key={item.topic}>
              <div className="W-unlock-top">
                <span>Phase {String(index + 1).padStart(2, "0")}</span>
                <b>{item.percentage}%</b>
              </div>

              <h3>{item.topic}</h3>

              <div className="W-day-pill">
                Day {item.startDay} – Day {item.endDay}
                <small>{item.totalDays} days</small>
              </div>

              <p>{item.focus}</p>

              <div className="W-checkpoint-list">
                {item.checkpoints.map((checkpoint) => (
                  <span key={checkpoint}>{checkpoint}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* EVERY TOPIC */}
      <section className="W-work-section">
        <div className="W-topic-system-box">
          <div className="W-work-heading W-compact">
            <p className="section-kicker">INSIDE EVERY TOPIC</p>

            <h2>Professionals do not just watch lectures. They build, practice, and improve.</h2>
          </div>

          <div className="W-topic-system-grid">
            {everyTopicIncludes.map((item, index) => (
              <div className="W-topic-system-card" key={item}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* JOB PREP */}
      <section className="W-work-section W-placement-journey-section">
        <div className="W-work-heading">
          <p className="section-kicker">AFTER THE 9-MONTH ROADMAP</p>

          <h2>
            From current job to stronger career opportunities, professionals follow a
            complete transition journey.
          </h2>

          <p>
            Career support is not limited to resume writing. Working professionals move
            through profile positioning, resume, LinkedIn, GitHub, project explanation,
            mock interviews, HR preparation, recruiter strategy, feedback, and final
            transition readiness.
          </p>
        </div>

        <div className="W-placement-journey">
          {jobSprint.map((phase, index) => (
            <div className="W-placement-step" key={phase.title}>
              <div className="W-placement-step-number">
                {String(index + 1).padStart(2, "0")}
              </div>

              <div className="W-placement-step-content">
                <div className="W-placement-visual">
                  <img src={phase.image} alt={phase.title} />
                </div>

                <div className="W-placement-text">
                  <span>Placement Step {index + 1}</span>
                  <h3>{phase.title}</h3>

                  <ul>
                    {phase.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* OUTCOME */}
      <section className="W-work-section">
        <div className="W-outcome-box">
          <p className="section-kicker">WHAT PROFESSIONALS BUILD</p>

          <h2>Professionals finish with upgrade-ready proof of skill.</h2>

          <p>
            By the end of the roadmap, serious working professionals are expected to
            build visible career assets that support switching, upgrading, or moving
            toward stronger AI, software, data, and full stack opportunities.
          </p>

          <div className="W-outcome-grid">
            <span>AI + Full Stack project portfolio</span>
            <span>GitHub profile with clean repositories</span>
            <span>Resume and LinkedIn upgrade readiness</span>
            <span>Deployed web applications</span>
            <span>Backend APIs with database integration</span>
            <span>AI / ML / GenAI project demos</span>
            <span>Project explanation confidence</span>
            <span>Interview and HR readiness</span>
            <span>Current experience positioning</span>
            <span>Job application strategy clarity</span>
            <span>Career switch or upgrade readiness</span>
            <span>Long-term career direction</span>
          </div>

          <button onClick={() => navigate("/counselling?roadmap=working-professionals")}>
            Apply for Counselling
          </button>
        </div>
      </section>
    </main>
  );
}